import { z } from 'zod'
import {
	CalculatedVariable,
	InterestVariable,
	RasterBands,
	Residue,
	SmartProcessingData,
} from './DashboardAgility.types'

export const InterestVariableSchema = z.enum(InterestVariable)

export const RasterBandsSchema: z.ZodType<RasterBands> = z
	.record(z.string(), z.string())
	.refine(
		(value) => Object.keys(value).every((key) => InterestVariableSchema.safeParse(key).success),
		{ message: 'bandas contém uma chave que não é uma InterestVariable válida' },
	) as unknown as z.ZodType<RasterBands>

export const ResidueApiSchema = z
	.object({
		plantio: z.string(),
		parcela: z.string(),
		valor_real: z.number(),
		valor_estimado: z.number(),
		residuo: z.number(),
	})
	.transform(
		(data): Residue => ({
			plantation: data.plantio,
			plot: data.parcela,
			actualValue: data.valor_real,
			estimatedValue: data.valor_estimado,
			residue: data.residuo,
		}),
	)

export const CalculatedVariableApiSchema = z
	.object({
		id: z.string(),
		id_estrato_inventario_florestal: z.string(),
		area: z.number(),
		variavel_de_interesse: InterestVariableSchema,
		modelo_ajustado_latex: z.string(),
		coeficientes_modelo_ajustado: z.array(z.number()),
		coeficiente_de_determinacao: z.number(),
		raiz_erro_quadratico_medio: z.number(),
		erro_absoluto_medio: z.number(),
		erro_absoluto_medio_percentual: z.number(),
		media_estimada: z.number(),
		limite_inferior_intervalo_confianca: z.number(),
		limite_superior_intervalo_confianca: z.number(),
		erro_amostragem: z.number(),
		erro_amostragem_percentual: z.number(),
		numero_parcelas_necessarias: z.number(),
		numero_parcelas_utilizadas: z.number(),
		erro_amostragem_percentual_inventario_tradicional: z.number(),
		numero_parcelas_necessarias_inventario_tradicional: z.number(),
		ganho_parcelas: z.number(),
		residuos: z.array(ResidueApiSchema),
		momento_geracao: z.string(),
	})
	.transform(
		(data): CalculatedVariable => ({
			id: data.id,
			forestInventoryStratumId: data.id_estrato_inventario_florestal,
			area: data.area,
			interestVariable: data.variavel_de_interesse,
			adjustedModelLatex: data.modelo_ajustado_latex,
			adjustedModelCoefficients: data.coeficientes_modelo_ajustado,
			determinationCoefficient: data.coeficiente_de_determinacao,
			rootMeanSquareError: data.raiz_erro_quadratico_medio,
			meanAbsoluteError: data.erro_absoluto_medio,
			meanAbsolutePercentageError: data.erro_absoluto_medio_percentual,
			estimatedMean: data.media_estimada,
			confidenceIntervalLowerBound: data.limite_inferior_intervalo_confianca,
			confidenceIntervalUpperBound: data.limite_superior_intervalo_confianca,
			samplingError: data.erro_amostragem,
			samplingErrorPercentage: data.erro_amostragem_percentual,
			requiredPlots: data.numero_parcelas_necessarias,
			usedPlots: data.numero_parcelas_utilizadas,
			traditionalSamplingErrorPercentage: data.erro_amostragem_percentual_inventario_tradicional,
			traditionalRequiredPlots: data.numero_parcelas_necessarias_inventario_tradicional,
			plotGain: data.ganho_parcelas,
			residues: data.residuos,
			generatedAt: data.momento_geracao,
		}),
	)

export const SmartProcessingDataApiSchema = z
	.object({
		id_customer: z.string(),
		identificador_raster: z.string(),
		bandas: RasterBandsSchema,
		variaveis_nao_calculadas: z.array(z.string()),
		variaveis_calculadas: z.array(CalculatedVariableApiSchema),
	})
	.transform(
		(data): SmartProcessingData => ({
			customerId: data.id_customer,
			rasterIdentifier: data.identificador_raster,
			bands: data.bandas,
			uncalculatedVariables: data.variaveis_nao_calculadas,
			calculatedVariables: data.variaveis_calculadas,
		}),
	)
